/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { useClickOutside } from "@/DesignSystem/Common/Hooks/useClickOutside";
import { NumberHelper } from "../../Helpers/NumberHelper";
import { ClickableTextPanel, IClickableTextPanelProps } from "./ClickableTextPanel";

import styles from "./FixedClickableTextPanel.module.scss";

interface IFixedClickableTextPanel extends IClickableTextPanelProps {
    closeOnOutsideClick?: boolean;
    isLinkDisabled: boolean;
    isClickableTextPanelOpen: boolean;
    isAboveLabel?: boolean;
    textboxHeight?: number;
    clickableTextPanelCssStyle?: React.CSSProperties;
    onClickableTextPanelOpen: () => void;
    onClickableTextPanelClose: () => void;
    linkClassName?: string;
    dataCy?: string;
}

export function FixedClickableTextPanel(props: IFixedClickableTextPanel): JSX.Element {
    const { getText } = useTranslation();
    const [topPanel, setTopPanel] = useState<number>(0);
    const [leftPanel, setLeftPanel] = useState<number>(0);
    const [isPanelVisible, setIsPanelVisible] = useState<boolean>(props.isClickableTextPanelOpen);
    const linkRef = useRef(null);
    const clickableTextPanelContainerRef = useRef(null);
    const containerRef = useRef(null);

    const setClickableTextPanelPosition = useCallback((refA: React.RefObject<HTMLElement>, refB: React.RefObject<HTMLElement>): void => {
        if (refA?.current && refB?.current) {
            const refBRect = refB.current.getBoundingClientRect();

            const top = Math.floor(refBRect.top);
            const right = Math.floor(refBRect.right);
            const width = Math.floor(refBRect.width);

            if (
                NumberHelper.isValidNumber(top) &&
                NumberHelper.isValidNumber(right) &&
                NumberHelper.isValidNumber(width)
            ) {
                const left = props.isAboveLabel ? right : (right - width);
                const calculatedTop: number = props.textboxHeight ? top - props.textboxHeight : top;

                setTopPanel(calculatedTop);
                setLeftPanel(left);
            }
        }
    }, [props.textboxHeight]);

    const runToSetClickableTextPosition = useCallback(() => setClickableTextPanelPosition(clickableTextPanelContainerRef, linkRef), [props.textboxHeight]);

    useEffect(() => {
        const element = document.getElementById(props.inputElementId);
        const resizeObserver = new ResizeObserver(() => {
            runToSetClickableTextPosition();
        });

        if (element) {
            resizeObserver.observe(element);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [props.inputElementId, runToSetClickableTextPosition]);

    useEffect(() => {
        if (props.isClickableTextPanelOpen) {
            setClickableTextPanelPosition(clickableTextPanelContainerRef, linkRef);
            setIsPanelVisible(true);
            document.addEventListener("scroll", runToSetClickableTextPosition, true);
        }
        else {
            setIsPanelVisible(false);
            document.removeEventListener("scroll", runToSetClickableTextPosition, true);
        }
    }, [props.isClickableTextPanelOpen]);

    useClickOutside(containerRef, () => {
        if (props.closeOnOutsideClick) {
            setIsPanelVisible(false);
            props.onClickableTextPanelClose?.();
        }
    });

    return (
        <>
            {props.isClickableTextPanelOpen && (
                <div ref={containerRef} className="clickable-text-container">
                    <div
                        ref={clickableTextPanelContainerRef}
                        className={`${styles.clickableTextPanelContainer} ${isPanelVisible ? "" : styles.hidden}`}
                        style={{ top: topPanel, left: leftPanel }}
                    >
                        <ClickableTextPanel
                            cssClass={props.cssClass}
                            context={props.context}
                            inputElementId={props.inputElementId}
                            onCloseEvent={() => props.onClickableTextPanelClose()}
                            reduceHeight={props.reduceHeight}
                            hasResponsiveHeight={props.hasResponsiveHeight}
                            cssStyle={props.clickableTextPanelCssStyle}
                            dataCy={props.dataCy ? `${props.dataCy}-clickableTextPanel` : null}
                        />
                    </div>
                </div>
            )}
            {!props.isLinkDisabled && (
                <div className={`text-right ${props.linkClassName}`}>
                    <a
                        ref={linkRef}
                        onClick={(evt: React.MouseEvent) => {
                            evt.stopPropagation();
                            props.onClickableTextPanelOpen();
                        }}
                        data-cy={props.dataCy ? `${props.dataCy}-select` : null}
                    >
                        {getText("SelectFromListLabel")}
                    </a>
                </div>
            )}
        </>
    );
}
